import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorProvider, useError } from './contexts/ErrorContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { setGlobalErrorHandler } from './services/api';
import Login from './components/Login';
import Header from './components/Header';
import BotControl from './components/BotControl';
import UserStats from './components/UserStats';
import News from './components/News';
import LogsViewer from './components/LogsViewer';
import ErrorNotification from './components/ErrorNotification';
import Users from './components/Users';
import Chats from './components/Chats';
import Application from './components/Application';
import './App.css';

const ErrorHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { addError } = useError();

  useEffect(() => {
    // Устанавливаем глобальный обработчик ошибок
    setGlobalErrorHandler(addError);
  }, [addError]);

  return <>{children}</>;
};

const Dashboard: React.FC = () => {
  return (
    <div className="dashboard">
      <Header title="Панель управления" />
      <main className="main-content">
        <div className="dashboard-grid">
          <div className="dashboard-section">
            <BotControl />
            <UserStats />
          </div>
          <div className="dashboard-section">
            <LogsViewer />
          </div>
        </div>
      </main>
    </div>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <div className="loading-text">Проверка авторизации...</div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

function App() {
  return (
    <ThemeProvider>
      <ErrorProvider>
        <ErrorHandler>
          <Router>
            <AuthProvider>
              <div className="App">
                <ErrorNotification />
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route 
                    path="/" 
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/users" 
                    element={
                      <ProtectedRoute>
                        <Users />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/chats" 
                    element={
                      <ProtectedRoute>
                        <Chats />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/broadcast" element={<Navigate to="/news" replace />} />
                  <Route 
                    path="/news" 
                    element={
                      <ProtectedRoute>
                        <News />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/application" 
                    element={
                      <ProtectedRoute>
                        <Application />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </AuthProvider>
          </Router>
        </ErrorHandler>
      </ErrorProvider>
    </ThemeProvider>
  );
}

export default App;
